import { useEffect, useRef } from 'react';
import { TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { HeaderSearch } from '@/components/features/HeaderSearch/HeaderSearch';
import { DesktopHeaderSearch } from '@/components/features/HeaderSearch/DesktopHeaderSearch';
import { TintableIcon } from '@/components/ui';
import { useIsDesktop } from '@/lib/notifications/useIsDesktop';
import { useSearchOverlayOpen } from '@/store';
import type { CategoryResponseDto } from '@/lib/models';

const TEST_ID_PREFIX = 'category-tabs';

export interface CategoryTabsProps {
  /** List of category names to render as tabs. */
  categories: CategoryResponseDto[];
  /** Currently selected category (must match one of `categories`). */
  activeCategory: string;
  /** Fired when a tab is clicked. */
  onCategoryChange?: (category: string) => void;
  className?: string;
  /**
   * Compact mode used when the home top bar is pinned to the viewport.
   * Reduces row height, tab font size, padding, and icon footprint.
   */
  compact?: boolean;
}

/**
 * Horizontally-scrollable category tab bar with a leading search icon
 * (lobby V2, Figma 5751-141388 "Tabs").
 *
 * Chips are transparent icon + capitalized-label pairs: the active tab is
 * brand-purple medium text, inactive tabs are grey regular text — no filled
 * pill. Overflow scrolls horizontally with hidden scrollbar.
 *
 * @example
 * ```tsx
 * <CategoryTabs
 *   categories={['Trending', 'Politics', 'Sports', 'Culture']}
 *   activeCategory="Trending"
 *   onCategoryChange={setCategory}
 * />
 * ```
 */
export function CategoryTabs({
  categories,
  activeCategory,
  onCategoryChange,
  className,
  compact = false,
}: CategoryTabsProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const searchOpen = useSearchOverlayOpen();
  // Desktop swaps the collapsed search icon for a default-open field with its
  // own results popover — the tabs stay put, so the mobile "tabs animate out
  // while search owns the row" treatment applies below md only.
  const isDesktop = useIsDesktop();
  const mobileSearchOpen = searchOpen && !isDesktop;

  useEffect(() => {
    const scroller = scrollRef.current;
    const activeEl = itemRefs.current[activeCategory];
    if (!scroller || !activeEl) return;

    const stopOffset = 15;
    const left = activeEl.offsetLeft - scroller.offsetLeft - scroller.clientLeft;
    const center = left + (activeEl.offsetWidth / 2 - scroller.offsetWidth / 2) + stopOffset;
    scroller.scrollTo({ left: center, behavior: 'smooth' });
  }, [activeCategory]);

  const rowHeight = compact ? 'h-11' : 'h-[58px]';

  return (
    <div className={cn('w-full', className)}>
      <div
        data-testid={TEST_ID_PREFIX}
        className={cn(
          // overflow-hidden clips the mobile expanding-field animation; the
          // desktop results popover must escape the row, so visible at md+.
          'relative flex items-center bg-transparent overflow-hidden md:overflow-visible transition-[height] duration-150',
          rowHeight,
        )}
      >
        {/* Search: mobile is a leading icon that opens in place (field overlays
            this row); desktop is a default-open field with an anchored popover. */}
        {isDesktop ? <DesktopHeaderSearch compact={compact} /> : <HeaderSearch compact={compact} />}

        {/* Scrollable tabs — staggered fade-up-out while mobile search is open. */}
        <div
          ref={scrollRef}
          data-testid={`${TEST_ID_PREFIX}-list`}
          aria-hidden={mobileSearchOpen || undefined}
          className={cn(
            'flex items-center overflow-x-auto px-2 pr-4 scrollbar-hide transition-[height] duration-150',
            rowHeight,
            compact ? 'gap-1.5' : 'gap-2',
            mobileSearchOpen && 'search-cats-out pointer-events-none',
          )}
        >
          {categories.map((cat) => {
            const isActive = cat.code === activeCategory;
            return (
              <div
                key={cat.code}
                data-testid={`${TEST_ID_PREFIX}-tab-${cat.code.toLowerCase()}`}
                ref={(el) => {
                  itemRefs.current[cat.code] = el;
                }}
                onClick={() => onCategoryChange?.(cat.code)}
                className={cn(
                  'font-sans capitalize whitespace-nowrap rounded-[40px] border-none',
                  'bg-transparent cursor-pointer transition-colors flex-shrink-0 flex justify-center items-center gap-1',
                  compact ? 'text-sm px-2 py-1' : 'text-lg px-2.5 py-1.5',
                  isActive ? 'font-medium text-purple' : 'font-normal text-ink-muted hover:text-purple',
                )}
              >
                {cat.iconImgUrl ? (
                  // Masked (not <img>) so the icon tints with the tab's text color.
                  <TintableIcon src={cat.iconImgUrl} className={compact ? 'size-4' : 'size-[18px]'} />
                ) : (
                  // The synthetic Trending tab has no admin icon — per Figma it carries a trend glyph.
                  cat.code === 'TRENDING' && (
                    <TrendingUp aria-hidden className={cn('flex-shrink-0', compact ? 'size-4' : 'size-[18px]')} />
                  )
                )}
                <span>{cat.displayName || cat.code}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
